import {
  ReceiptAcknowledgementItem,
  RetentionCalculationInput,
  TaxCalculationResult,
  TaxRetentionRule,
} from './types.ts';

/**
 * Resolve the TaxRetention rule for a line item.
 * Mirrors Backend.BusinessRules.ReceiptAcknowledgements.RARetentionCalculationActuator.ResolveRule
 *
 * Precedence:
 * 1) ProviderType + TaxRegime + EconomicActivity
 * 2) ProviderType + TaxRegime + EconomicActivity=null
 * 3) ProviderType + TaxRegime=null + EconomicActivity
 * 4) ProviderType + TaxRegime=null + EconomicActivity=null
 */
export const resolveRetentionRule = (
  companyRetentions: TaxRetentionRule[],
  providerTypeId: number | null | undefined,
  providerTaxRegimeId: number | null | undefined,
  expenseAccountId: number
): TaxRetentionRule | null => {
  if (providerTypeId == null) return null;

  return (
    companyRetentions.find(
      (x) =>
        x.providerTypeId === providerTypeId &&
        x.taxRegimeId === providerTaxRegimeId &&
        x.economicActivityId === expenseAccountId
    ) ??
    companyRetentions.find(
      (x) =>
        x.providerTypeId === providerTypeId &&
        x.taxRegimeId === providerTaxRegimeId &&
        x.economicActivityId == null
    ) ??
    companyRetentions.find(
      (x) =>
        x.providerTypeId === providerTypeId &&
        x.taxRegimeId == null &&
        x.economicActivityId === expenseAccountId
    ) ??
    companyRetentions.find(
      (x) =>
        x.providerTypeId === providerTypeId &&
        x.taxRegimeId == null &&
        x.economicActivityId == null
    ) ??
    null
  );
};

/**
 * Calculate total IVA from items (item amount * taxRate / 100)
 */
export const calculateTotalIVA = (items: ReceiptAcknowledgementItem[]): number => {
  if (!items || items.length === 0) return 0;

  return items.reduce((total, item) => {
    const amount = parseFloat(String(item.amount)) || 0;
    const taxRate = parseFloat(String(item.taxRate)) || 0;
    return total + amount * (taxRate / 100);
  }, 0);
};

/**
 * Calculate retained ISR / IVA using TaxRetention catalogue rules.
 * Mirrors RARetentionCalculationActuator.ExecuteAsync
 */
export const calculateRetentionsFromRules = (
  taxRetentions: TaxRetentionRule[],
  providerTypeId: number | null | undefined,
  providerTaxRegimeId: number | null | undefined,
  items: ReceiptAcknowledgementItem[]
): { retainedISR: number; retainedIVA: number } => {
  let retainedISR = 0;
  let retainedIVA = 0;

  if (!items?.length || !taxRetentions?.length) {
    return { retainedISR, retainedIVA };
  }

  for (const item of items) {
    const expenseAccountId = parseInt(String(item.expenseAccountId), 10);
    if (!expenseAccountId) continue;

    const itemRule = resolveRetentionRule(
      taxRetentions,
      providerTypeId,
      providerTaxRegimeId,
      expenseAccountId
    );

    if (!itemRule) continue;

    const amount = parseFloat(String(item.amount)) || 0;
    retainedISR += amount * (itemRule.isrRate / 100);
    retainedIVA += amount * (itemRule.ivaRate / 100);
  }

  return { retainedISR, retainedIVA };
};

/**
 * Calculate all tax-related amounts for a receipt acknowledgement summary preview.
 * Totals match Backend.Models.ReceiptAcknowledgements.ReceiptAcknowledgement + SetRetentionValues.
 */
export const calculateReceiptAcknowledgementTaxes = (
  input: RetentionCalculationInput
): TaxCalculationResult => {
  const { taxRetentions, providerTypeId, providerTaxRegimeId, items, subtotal } = input;

  const { retainedISR, retainedIVA } = calculateRetentionsFromRules(
    taxRetentions,
    providerTypeId,
    providerTaxRegimeId,
    items
  );
  const totalIVA = calculateTotalIVA(items);
  const totalRetention = retainedISR + retainedIVA;
  const paymentTotal = subtotal + totalIVA - totalRetention;
  const total = subtotal + totalIVA;

  return {
    subtotal,
    retainedISR,
    retainedIVA,
    totalIVA,
    totalRetention,
    paymentTotal,
    total,
  };
};
